import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { WorkflowAction } from '../../../../../api/types/dasTickets.types';

interface TicketHistoryProps {
  workflowHistory: WorkflowAction[];
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getActionColor = (action: string) => {
  switch (action.toLowerCase()) {
    case 'created':
      return '#3b82f6';
    case 'submitted':
      return '#3b82f6';
    case 'resubmitted':
      return '#8b5cf6'; // Purple for resubmit
    case 'approved':
    case 'finalapproved':
      return '#10b981';
    case 'rejected':
    case 'finalrejected':
      return '#ef4444';
    case 'recommended':
      return '#8b5cf6';
    case 'escalated':
      return '#f59e0b';
    case 'returned':
    case 'returnedforclarification':
      return '#f97316';
    case 'commented':
    case 'commentadded':
      return '#64748b';
    // Two-Level Approval / Consolidation actions
    case 'preapproved':
      return '#22c55e'; // Green for pre-approval
    case 'consolidationcreated':
      return '#6366f1'; // Indigo for consolidation created
    case 'addedtoconsolidation':
      return '#8b5cf6'; // Purple
    case 'removedfromconsolidation':
      return '#f97316'; // Orange
    case 'consolidationundone':
      return '#f59e0b'; // Amber
    case 'approveroverridden':
      return '#0ea5e9'; // Sky blue for approver change
    case 'externalreferenceupdated':
      return '#0ea5e9'; // Sky blue for administrative metadata change
    default:
      return '#94a3b8';
  }
};

// Clean up comments to remove duplicates and category prefixes
const cleanComments = (comments: string | undefined): string => {
  if (!comments) return '';

  let cleaned = comments;

  // Remove [Resubmitted] prefix as it's shown in the action badge
  cleaned = cleaned.replace(/^\[Resubmitted\]\s*/i, '');

  // Remove category prefix like "[Clarification Needed]", "[Missing Attachments]", etc.
  cleaned = cleaned.replace(/^\[[\w\s]+\]\s*/i, '');

  // Split by ". " and remove duplicates while preserving order
  const parts = cleaned.split('. ').filter(Boolean);
  const uniqueParts: string[] = [];
  const seen = new Set<string>();

  for (const part of parts) {
    const normalized = part.trim().toLowerCase();
    if (!seen.has(normalized) && part.trim()) {
      seen.add(normalized);
      uniqueParts.push(part.trim());
    }
  }

  return uniqueParts.join('. ');
};

const getActionIcon = (action: string) => {
  switch (action.toLowerCase()) {
    case 'created':
    case 'submitted':
      return <IconSystem.file size={16} />;
    case 'resubmitted':
      return <IconSystem.refresh size={16} />;
    case 'approved':
    case 'finalapproved':
      return <IconSystem.check size={16} />;
    case 'rejected':
    case 'finalrejected':
      return <IconSystem.x size={16} />;
    case 'returned':
    case 'returnedforclarification':
      return <IconSystem.alert size={16} />;
    case 'commented':
    case 'commentadded':
      return <IconSystem.edit size={16} />;
    // Two-Level Approval / Consolidation actions
    case 'preapproved':
      return <IconSystem.check size={16} />;
    case 'consolidationcreated':
      return <IconSystem.file size={16} />;
    case 'addedtoconsolidation':
      return <IconSystem.link size={16} />;
    case 'removedfromconsolidation':
      return <IconSystem.x size={16} />;
    case 'consolidationundone':
      return <IconSystem.refresh size={16} />;
    case 'approveroverridden':
      return <IconSystem.user size={16} />;
    case 'externalreferenceupdated':
      return <IconSystem.edit size={16} />;
    default:
      return <IconSystem.clock size={16} />;
  }
};

// Helper to detect if an action is a resubmission based on comments
const getDisplayAction = (action: string, comments?: string): string => {
  // Check if comments indicate this is a resubmission
  if (comments?.startsWith('[Resubmitted]')) {
    return 'Resubmitted';
  }
  // Normalize action names for display
  switch (action.toLowerCase()) {
    case 'returnedforclarification':
      return 'Returned';
    case 'commentadded':
      return 'Commented';
    case 'statuschanged':
      return 'Updated';
    // Two-Level Approval / Consolidation actions
    case 'preapproved':
      return 'Pre-Approved';
    case 'consolidationcreated':
      return 'Consolidation Created';
    case 'addedtoconsolidation':
      return 'Added to Consolidation';
    case 'removedfromconsolidation':
      return 'Removed from Consolidation';
    case 'consolidationundone':
      return 'Consolidation Undone';
    case 'finalapproved':
      return 'Final Approved';
    case 'finalrejected':
      return 'Final Rejected';
    case 'approveroverridden':
      return 'Approver Changed';
    case 'externalreferenceupdated':
      return 'External Reference Updated';
    default:
      return action;
  }
};

export const TicketHistory: React.FC<TicketHistoryProps> = ({ workflowHistory }) => {
  if (!workflowHistory || workflowHistory.length === 0) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          No history available for this ticket yet.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Workflow History
        </h3>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Complete timeline of all actions taken on this ticket
        </p>
      </div>

      {/* Timeline */}
      <div style={{ position: 'relative', paddingLeft: '2rem' }}>
        {/* Timeline Line */}
        <div
          style={{
            position: 'absolute',
            left: '15px',
            top: '20px',
            bottom: '20px',
            width: '2px',
            background: 'var(--border-color)',
          }}
        />

        {/* Timeline Items */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {workflowHistory.map((item) => {
          // Detect resubmissions from comments prefix
          const displayAction = getDisplayAction(item.action, item.comments);

          return (
            <div key={item.id} style={{ position: 'relative' }}>
              {/* Timeline Dot */}
              <div
                style={{
                  position: 'absolute',
                  left: '-2rem',
                  top: '4px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: getActionColor(displayAction),
                  border: '3px solid var(--bg-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  zIndex: 1,
                }}
              >
                {getActionIcon(displayAction)}
              </div>

              {/* Content Card */}
              <div
                style={{
                  background: 'var(--bg-secondary)',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    marginBottom: '0.5rem',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span
                        style={{
                          padding: '0.25rem 0.625rem',
                          background: getActionColor(displayAction),
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          borderRadius: '0.25rem',
                          textTransform: 'uppercase',
                        }}
                      >
                        {displayAction}
                      </span>
                      {item.level && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          Level {item.level}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {item.actionBy || 'Unknown'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{item.actionByRole}</div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {formatDate(item.actionDate)}
                  </div>
                </div>

                {item.comments && cleanComments(item.comments) && (
                  <div
                    style={{
                      marginTop: '0.75rem',
                      paddingTop: '0.75rem',
                      borderTop: '1px solid var(--border-color)',
                      fontSize: '0.875rem',
                      color: 'var(--text-primary)',
                      lineHeight: 1.5,
                    }}
                  >
                    {cleanComments(item.comments)}
                  </div>
                )}
              </div>
            </div>
          );
          })}
        </div>
      </div>
    </div>
  );
};

export default TicketHistory;
